import PageLayout from "@/components/feature/PageLayout";
import PageHero from "@/components/feature/PageHero";
import PageCTA from "@/components/feature/PageCTA";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FAQSection from "@/components/feature/FAQSection";
import AnimatedSection from "@/components/AnimatedSection";
import { servicesPageData } from "@/mocks/servicesPageData";
import { processData } from "@/mocks/homeData";
import { servicesEngagementData } from "@/mocks/servicesEngagementData";

function ServiceCategoryCard({
  service,
  index,
}: {
  service: (typeof servicesPageData.categories)[0];
  index: number;
}) {
  return (
    <AnimatedSection delay={index * 100}>
      <div
        className={`group relative p-6 md:p-7 rounded-2xl border transition-all duration-400 card-lift h-full flex flex-col ${
          service.featured
            ? "bg-gradient-to-br from-primary-800 to-primary-900 border-primary-700/60 hover:border-accent-400/40 shadow-lg shadow-primary-900/10"
            : "bg-background-100 border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5"
        }`}
      >
        {service.featured && (
          <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-accent-500 text-background-50 text-[10px] font-bold uppercase tracking-wider">
            Primary
          </div>
        )}

        <div
          className={`w-[52px] h-[52px] flex items-center justify-center rounded-xl mb-5 transition-all duration-300 ${
            service.featured
              ? "bg-accent-500/15 text-accent-400 group-hover:bg-accent-500 group-hover:text-background-50"
              : "bg-primary-50 text-primary-600 group-hover:bg-accent-500 group-hover:text-background-50"
          }`}
        >
          <span className="w-6 h-6 flex items-center justify-center">
            <i className={`${service.icon} text-xl`} />
          </span>
        </div>

        <h3
          className={`text-base md:text-lg font-bold mb-2.5 font-heading ${
            service.featured ? "text-background-50" : "text-foreground-950"
          }`}
        >
          {service.title}
        </h3>
        <p
          className={`text-sm leading-relaxed mb-5 flex-grow ${
            service.featured ? "text-background-400" : "text-foreground-600"
          }`}
        >
          {service.desc}
        </p>

        <div className="mb-5">
          <p
            className={`text-xs font-semibold uppercase tracking-wider mb-2.5 ${
              service.featured ? "text-accent-400" : "text-foreground-500"
            }`}
          >
            Key Capabilities
          </p>
          <ul className="space-y-1.5">
            {service.capabilities.slice(0, 5).map((cap) => (
              <li
                key={cap}
                className={`flex items-center gap-2 text-sm ${
                  service.featured ? "text-background-400" : "text-foreground-600"
                }`}
              >
                <i
                  className={`ri-check-line text-xs ${
                    service.featured ? "text-accent-400" : "text-accent-500"
                  }`}
                />
                {cap}
              </li>
            ))}
          </ul>
        </div>

        <a
          href={service.href}
          className={`inline-flex items-center gap-1.5 text-sm font-semibold transition-colors btn-arrow-hover group/link ${
            service.featured
              ? "text-accent-400 hover:text-accent-300"
              : "text-accent-600 hover:text-accent-700"
          }`}
        >
          {service.cta}
          <i className="ri-arrow-right-line btn-arrow text-xs" />
        </a>
      </div>
    </AnimatedSection>
  );
}

function ServiceDetailRow({
  title,
  items,
  cta,
  href,
  color = "accent",
}: {
  title: string;
  items: string[];
  cta: string;
  href: string;
  color?: "accent" | "secondary" | "primary";
}) {
  const colorClasses = {
    accent: "text-accent-600 bg-accent-50",
    secondary: "text-secondary-600 bg-secondary-50",
    primary: "text-primary-600 bg-primary-50",
  };

  return (
    <div className="py-12 md:py-16 border-t border-background-200/50">
      <div className="flex flex-col md:flex-row md:items-start gap-6 md:gap-10">
        <div className="md:w-64 shrink-0">
          <h3 className="text-xl font-bold text-foreground-950 font-heading mb-2">
            {title}
          </h3>
          <a
            href={href}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-600 hover:text-accent-700 transition-colors btn-arrow-hover group"
          >
            {cta}
            <i className="ri-arrow-right-line btn-arrow text-xs" />
          </a>
        </div>
        <div className="flex flex-wrap gap-2">
          {items.map((item) => (
            <span
              key={item}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium ${colorClasses[color]} border border-background-200/50`}
            >
              <i className="ri-check-line text-xs" />
              {item}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ServicesPage() {
  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Services" },
        ]}
      />

      <PageHero
        badge={servicesPageData.hero.badge}
        headline={servicesPageData.hero.headline}
        subtext={servicesPageData.hero.subtext}
        primaryCTA={servicesPageData.hero.primaryCTA}
        secondaryCTA={servicesPageData.hero.secondaryCTA}
      />

      {/* Service Categories */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Services Overview
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {servicesPageData.categories.map((service, i) => (
              <ServiceCategoryCard key={service.id} service={service} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Service Detail Rows */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Services at a Glance
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <ServiceDetailRow
            title="Laravel & Backend"
            items={[
              "Laravel Development",
              "Custom Laravel Development",
              "Laravel API Development",
              "Laravel SaaS Development",
              "Laravel Enterprise Applications",
              "Laravel Migration",
              "Laravel Performance Optimization",
              "Laravel Maintenance",
              "Hire Laravel Developers",
            ]}
            cta="Explore Laravel Development"
            href="/services/laravel-development"
            color="accent"
          />
          <ServiceDetailRow
            title="Web Development"
            items={[
              "Custom Web Applications",
              "PHP Development",
              "Vue.js Development",
              "React Development",
              "Admin Panels",
              "CRM Development",
              "ERP Development",
              "Enterprise Applications",
            ]}
            cta="Explore Web Development"
            href="/services/web-development"
            color="secondary"
          />
          <ServiceDetailRow
            title="Mobile Development"
            items={[
              "Flutter Development",
              "Android Development",
              "iOS Development",
              "Cross-platform Applications",
              "Mobile APIs",
              "Push Notifications",
            ]}
            cta="Explore Mobile Development"
            href="/services/mobile-app-development"
            color="primary"
          />
          <ServiceDetailRow
            title="AI Development"
            items={[
              "AI Integration",
              "AI-Powered Applications",
              "ChatGPT Integration",
              "Claude Integration",
              "AI Chatbots",
              "AI Assistants",
              "AI Workflow Automation",
              "AI API Integration",
              "AI-powered SaaS",
            ]}
            cta="Explore AI Development"
            href="/services/ai-development"
            color="accent"
          />
          <ServiceDetailRow
            title="API Development"
            items={[
              "REST API Development",
              "Laravel APIs",
              "Mobile APIs",
              "Third-party Integrations",
              "Payment APIs",
              "Authentication & Authorization",
              "API Security",
              "API Performance",
            ]}
            cta="Explore API Development"
            href="/services/api-development"
            color="secondary"
          />
          <ServiceDetailRow
            title="SaaS Development"
            items={[
              "SaaS Product Development",
              "Multi-tenancy",
              "Subscription Management",
              "Payment Integration",
              "User Management",
              "Roles & Permissions",
              "Admin Dashboards",
              "Analytics & Notifications",
              "Scalable Architecture",
            ]}
            cta="Explore SaaS Development"
            href="/services/saas-development"
            color="primary"
          />
          <ServiceDetailRow
            title="Cloud & DevOps"
            items={[
              "AWS",
              "Cloud Deployment",
              "Docker",
              "CI/CD",
              "Cloudflare",
              "Monitoring",
              "Backups",
              "Security",
              "Performance",
              "Infrastructure",
            ]}
            cta="Explore Cloud & DevOps"
            href="/services/cloud-devops"
            color="accent"
          />
          <ServiceDetailRow
            title="UI/UX Design"
            items={[
              "UI/UX Design",
              "Web Design",
              "Mobile UI",
              "Dashboard Design",
              "SaaS Product Design",
              "Design Systems",
            ]}
            cta="Explore UI/UX Design"
            href="/services/ui-ux-design"
            color="secondary"
          />
          <ServiceDetailRow
            title="Maintenance & Support"
            items={[
              "Bug Fixing",
              "Application Maintenance",
              "Performance Optimization",
              "Security Updates",
              "Server Maintenance",
              "Long-Term Support",
            ]}
            cta="Explore Maintenance & Support"
            href="/services/maintenance-support"
            color="primary"
          />
        </div>
      </section>

      {/* How We Work With You */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                {servicesEngagementData.heading}
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                {servicesEngagementData.subtext}
              </p>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto mt-5" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {servicesEngagementData.items.map((item, i) => (
              <AnimatedSection key={item.title} delay={i * 100}>
                <div className="group p-7 md:p-8 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full flex flex-col">
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                      <span className="w-6 h-6 flex items-center justify-center">
                        <i className={`${item.icon} text-xl`} />
                      </span>
                    </div>
                    <span className="inline-flex px-3 py-1 rounded-full bg-accent-500/10 text-accent-600 text-[10px] font-bold uppercase tracking-wider">
                      {item.highlight}
                    </span>
                  </div>

                  <h3 className="text-lg font-bold text-foreground-950 mb-3 font-heading">
                    {item.title}
                  </h3>

                  <div className="space-y-3 mb-5">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-1">
                        Best For
                      </p>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.bestFor}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-1">
                        Engagement
                      </p>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.engagement}</p>
                    </div>
                  </div>

                  <ul className="space-y-1.5 mb-6">
                    {item.benefits.map((benefit) => (
                      <li key={benefit} className="flex items-center gap-2 text-sm text-foreground-600">
                        <i className="ri-check-line text-xs text-accent-500" />
                        {benefit}
                      </li>
                    ))}
                  </ul>

                  <a
                    href="/contact-us"
                    className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-accent-600 hover:text-accent-700 transition-colors btn-arrow-hover group/link"
                  >
                    Start With This Model
                    <i className="ri-arrow-right-line btn-arrow text-xs" />
                  </a>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-28 bg-background-50 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="container-astro relative z-10">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Why Choose AstroJal
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {servicesPageData.whyChooseUs.map((item, i) => (
              <AnimatedSection key={item.title} delay={i * 100}>
                <div className="group p-7 md:p-8 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-2.5 font-heading">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Development Process */}
      <section className="py-20 md:py-28 bg-background-100">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Our Development Process
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute top-[52px] left-[7%] right-[7%] h-px bg-gradient-to-r from-accent-500/20 via-accent-500/40 to-accent-500/20" />
              <div className="grid grid-cols-7 gap-4">
                {processData.steps.map((step, i) => (
                  <AnimatedSection key={step.number} delay={i * 120}>
                    <div className="text-center group">
                      <div className="relative inline-flex items-center justify-center w-[52px] h-[52px] rounded-full bg-background-100 border-2 border-accent-500/30 text-accent-600 font-extrabold text-sm font-heading mb-5 mx-auto group-hover:bg-accent-500 group-hover:text-background-50 group-hover:border-accent-500 transition-all duration-300 z-10">
                        {step.number}
                      </div>
                      <h3 className="text-sm font-bold text-foreground-950 mb-2 font-heading">
                        {step.title}
                      </h3>
                      <p className="text-xs text-foreground-500 leading-relaxed max-w-[140px] mx-auto">
                        {step.desc}
                      </p>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:hidden">
            <div className="relative max-w-md mx-auto">
              <div className="absolute left-[25px] top-0 bottom-0 w-px bg-gradient-to-b from-accent-500/30 via-accent-500/50 to-accent-500/30" />
              <div className="flex flex-col gap-8">
                {processData.steps.map((step, i) => (
                  <AnimatedSection key={step.number} delay={i * 100}>
                    <div className="flex items-start gap-5 group">
                      <div className="relative z-10 flex items-center justify-center w-[52px] h-[52px] rounded-full bg-background-100 border-2 border-accent-500/30 text-accent-600 font-extrabold text-sm font-heading shrink-0 group-hover:bg-accent-500 group-hover:text-background-50 group-hover:border-accent-500 transition-all duration-300">
                        {step.number}
                      </div>
                      <div className="pt-2.5">
                        <h3 className="text-base font-bold text-foreground-950 mb-1.5 font-heading">
                          {step.title}
                        </h3>
                        <p className="text-sm text-foreground-500 leading-relaxed">
                          {step.desc}
                        </p>
                      </div>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Technology Stack
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {[
              { name: "Laravel", icon: "ri-code-s-slash-line", color: "text-red-500" },
              { name: "PHP", icon: "ri-code-box-line", color: "text-purple-500" },
              { name: "Vue.js", icon: "ri-vuejs-line", color: "text-green-500" },
              { name: "React", icon: "ri-reactjs-line", color: "text-blue-400" },
              { name: "Flutter", icon: "ri-flutter-line", color: "text-cyan-400" },
              { name: "MySQL", icon: "ri-database-2-line", color: "text-blue-600" },
              { name: "PostgreSQL", icon: "ri-database-2-line", color: "text-indigo-500" },
              { name: "Redis", icon: "ri-server-line", color: "text-red-400" },
              { name: "AWS", icon: "ri-cloud-line", color: "text-orange-400" },
              { name: "Docker", icon: "ri-ship-line", color: "text-blue-500" },
              { name: "Git", icon: "ri-git-branch-line", color: "text-red-500" },
              { name: "REST APIs", icon: "ri-exchange-line", color: "text-accent-500" },
            ].map((tech, i) => (
              <AnimatedSection key={tech.name} delay={i * 60}>
                <div className="flex flex-col items-center gap-3 p-5 md:p-6 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-300 card-lift">
                  <span className={`w-10 h-10 flex items-center justify-center ${tech.color}`}>
                    <i className={`${tech.icon} text-2xl`} />
                  </span>
                  <span className="text-sm font-semibold text-foreground-800">{tech.name}</span>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Client Outcomes */}
      <section className="py-20 md:py-28 bg-background-100 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="container-astro relative z-10">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Client Outcomes
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {servicesPageData.clientOutcomes.map((item, i) => (
              <AnimatedSection key={item.number} delay={i * 100}>
                <div className="group p-7 md:p-8 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br ${item.gradient} text-background-50 mb-5`}>
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-2.5 font-heading">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <FAQSection items={servicesPageData.faq} />

      <PageCTA
        heading={servicesPageData.cta.heading}
        text={servicesPageData.cta.text}
        primaryCTA={servicesPageData.cta.primaryCTA}
        secondaryCTA={servicesPageData.cta.secondaryCTA}
      />
    </PageLayout>
  );
}